import { useEffect, useState } from 'react'
import type { RefObject } from 'react'
import type { MapCanvasHandle } from './MapCanvas'
import { cabinetApi, type SstGridResponse } from '../api'
import { useViewCtx } from '../context/ViewContext'
import { toContourGrid, clampLevel, ISO_MIN, ISO_MAX } from '../maps/isothermMath'
import { buildIsothermFamily, isothermFamilyLevels, temperatureGridPlan } from './isothermFamily'
import { isothermSurfaceColor, isothermLineColor } from './isotherms'
import { detectFronts } from '../maps/fronts'
import { ISOTHERM_LAYER, overlayGridKey } from './overlayLayers'
import { gridHasData, gridIsPartial } from '../maps/gridEmpty'
import { toPer100km } from '../maps/frontsMath'
import { featureDrawn } from './drawn'
import { toolPending } from './toolPending'
import type { ToolsView } from './seriesOverride'

// Единый ключ слоя для инструмента температуры (общий над разделами).
const TEMP_KEY = 'temp'
/** Поверхностная изотерма живёт на СВОЁМ ключе слоёв.
 *
 *  Не общая коллекция с глубинным семейством: сетки у них разные и приезжают
 *  порознь, и общий источник на четверть секунды показывал бы поверхность
 *  прошлого кадра рядом с глубиной нового. Плюс выключается такая линия одним
 *  движением — `removeIsotherms(SURFACE_KEY)`. */
const SURFACE_KEY = 'temp-surface'

/**
 * `override` — значения кадра серии: она снимает свои пять вьюпортов, НЕ трогая
 * общий контекст, иначе адресная строка и «Поделиться» посреди прогона отдали бы
 * чужой вид (см. `seriesOverride`).
 *
 * Единый инструмент «Изотермы + градиенты» на карте раздела. Общий над «Картами» и
 * «Аналитикой»: состояние (isotherm/fronts/frontsUnit) — в ViewContext, диапазон
 * фиксированный 5–16 °C, работает над ЛЮБЫМ базовым слоем. Сетку SST на вьюпорт
 * грузим один раз (на смену даты/глубины/области И по завершении докачки —
 * `loadNonce`, см. overlayGridKey), ползунки пересчитывают линии в браузере —
 * без сети. Температуру берём на глубине Контекста.
 */
export function useTemperatureTools(
  mapRef: RefObject<MapCanvasHandle | null>,
  viewKey: string,
  loadNonce = 0,
  override: ToolsView | null = null,
): {
  sstData: boolean
  sstPartial: boolean
  isothermDrawn: boolean
  frontsDrawn: boolean
  /** Есть ли сетка ПОВЕРХНОСТИ в кадре. Отдельно от `sstData`: глубинной сетки
   *  может не быть вовсе (её инструменты выключены). */
  surfaceData: boolean
  /** Легла ли поверхностная линия на карту. */
  surfaceDrawn: boolean
  /** Сетка ещё не отвечает на текущий запрос — кадр серии снимать рано. */
  pending: boolean
} {
  const ctx = useViewCtx()
  const setIsotherm = ctx.setIsotherm
  const date = ctx.date
  const depth = override?.depth ?? ctx.depth
  const isotherm = override ? override.isotherm : ctx.isotherm
  const isothermSurface = override ? override.isothermSurface : ctx.isothermSurface
  const isothermSpread = override ? override.isothermSpread : ctx.isothermSpread
  const fronts = override ? override.fronts : ctx.fronts
  const frontsUnit = override ? override.frontsUnit : ctx.frontsUnit
  const [sstGrid, setSstGrid] = useState<SstGridResponse | null>(null)
  // Сетка ПОВЕРХНОСТИ (0 м) — вторая, своя. Запрашивается только когда контекст
  // стоит не на поверхности: на 0 м это та же самая сетка, и второй запрос был
  // бы одним и тем же байтом дважды.
  const [surfGrid, setSurfGrid] = useState<SstGridResponse | null>(null)
  // Закрывает ли поставка кадр ЦЕЛИКОМ. Спрашиваем сервер (тот же критерий
  // охвата, что у докачки): по самой сетке этого не понять — суша тоже NULL.
  // undefined — не спрашивали/не ответили, тогда молчим.
  const [sstCovered, setSstCovered] = useState<boolean | undefined>(undefined)
  // Под какой запрос лежит сетка. Флага «идёт загрузка» мало: между запросом и
  // ответом хук держит ПРЕЖНЮЮ сетку, и «данные есть» на её счёт — правда о
  // прошлом кадре (см. toolPending).
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const [surfLoadedKey, setSurfLoadedKey] = useState<string | null>(null)
  // Что реально легло на карту. Сетка может быть полной, а линий — ни одной:
  // изотермы 5 °C нет в воде 7,6…11,5 °C (прод 2026-08-09). Легенда об этом
  // говорит вслух, и признак берётся из самой геометрии, а не считается заново.
  const [isothermDrawn, setIsothermDrawn] = useState(true)
  const [frontsDrawn, setFrontsDrawn] = useState(true)
  const [surfaceDrawn, setSurfaceDrawn] = useState(true)
  const isoEnabled = isotherm != null
  const frontsEnabled = fronts != null
  const surfaceEnabled = isothermSurface != null
  // Какие сетки нужны — решает чистое правило (temperatureGridPlan): поверхность
  // на 0 м читает ту же сетку, и второй запрос был бы тем же байтом дважды.
  const { depthGrid: gridEnabled, surfaceGrid: surfaceGridEnabled } = temperatureGridPlan({
    isoEnabled,
    frontsEnabled,
    surfaceEnabled,
    depth,
  })
  // Поверхность на 0 м читает ту же сетку, что и всё остальное.
  const surfaceSource = depth === 0 ? sstGrid : surfGrid

  useEffect(() => {
    const map = mapRef.current
    if (!map || (!gridEnabled && !surfaceGridEnabled)) {
      setSstGrid(null)
      setSurfGrid(null)
      setSstCovered(undefined)
      setLoadedKey(null)
      setSurfLoadedKey(null)
      return
    }
    let alive = true
    // Дебаунс: в «Аналитике» ползунок дней меняет date на каждый шаг — без задержки
    // перетаскивание превратилось бы в шторм запросов сетки.
    const id = setTimeout(() => {
      const bbox = map.getBBox()
      const key = overlayGridKey({ date, depth, viewKey, loadNonce })
      const surfKey = overlayGridKey({ date, depth: 0, viewKey, loadNonce })
      if (gridEnabled) {
        cabinetApi
          .sstGrid({ date, depth, bbox })
          .then(g => {
            if (!alive) return
            setSstGrid(g)
            setLoadedKey(key)
          })
          // Отказ — тоже ответ на этот запрос: иначе серия ждала бы сетку,
          // которой не будет, весь отведённый ей потолок.
          .catch(() => {
            if (!alive) return
            setSstGrid(null)
            setLoadedKey(key)
          })
        // Охват — вторым, независимым запросом: он лёгкий (read-only, задачи не
        // создаёт) и его отказ не имеет права оставить карту без линий.
        cabinetApi
          .availability({ layer: ISOTHERM_LAYER, date, depth, bbox })
          .then(a => alive && setSstCovered(a.data_present))
          .catch(() => alive && setSstCovered(undefined))
      }
      // Поверхность — тем же дебаунсом и тем же поколением `alive`: два таймера
      // дали бы на ползунке дней два независимых шторма запросов.
      if (surfaceGridEnabled) {
        cabinetApi
          .sstGrid({ date, depth: 0, bbox })
          .then(g => {
            if (!alive) return
            setSurfGrid(g)
            setSurfLoadedKey(surfKey)
          })
          .catch(() => {
            if (!alive) return
            setSurfGrid(null)
            setSurfLoadedKey(surfKey)
          })
        // Охват отдельно не спрашиваем: обрыв поставки — свойство даты и
        // района, а не горизонта, и лишний запрос на кадр серии дороже.
      }
    }, 250)
    return () => {
      alive = false
      clearTimeout(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Зависимость — ТУМБЛЕР поверхности (boolean), а не её градус: иначе
    // движение второго ползунка ходило бы в сеть, ради чего инструмент и
    // считает линии в браузере.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridEnabled, surfaceGridEnabled, overlayGridKey({ date, depth, viewKey, loadNonce })])

  // Новая сетка → прижать градус в фикс. границы 5–16 (не data-driven).
  useEffect(() => {
    // Под серией в общий контекст не пишем ВООБЩЕ: значение там пользовательское,
    // и правка его ради кадра ушла бы в адресную строку и пережила бы серию.
    if (override || isotherm == null) return
    const clamped = clampLevel(isotherm, ISO_MIN, ISO_MAX)
    if (clamped !== isotherm) setIsotherm(clamped)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sstGrid, override])

  useEffect(() => {
    if (override || isothermSurface == null) return
    const clamped = clampLevel(isothermSurface, ISO_MIN, ISO_MAX)
    if (clamped !== isothermSurface) ctx.setIsothermSurface(clamped)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surfGrid, sstGrid, override])

  // Семейство глубинных изотерм: основная плюс смежные, если включён их шаг.
  // Пересчёт на смену сетки, градуса ИЛИ шага — мгновенно, по кэшу, без сети.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const grid = isoEnabled ? toContourGrid(sstGrid) : null
    if (!grid || isotherm == null) {
      map.removeIsotherms(TEMP_KEY)
      setIsothermDrawn(true)
      return
    }
    const levels = isothermFamilyLevels(isotherm, isothermSpread, sstGrid?.vmin, sstGrid?.vmax)
    const { contours, labels } = buildIsothermFamily(grid, levels, isotherm, isothermLineColor(), {
      // Ярлыки появляются ВМЕСТЕ со смежными: одиночная изотерма выглядит ровно
      // как до появления тумблера, и прежние карты не меняются задним числом.
      labels: isothermSpread != null,
    })
    // «Легла ли изотерма» — про ОСНОВНУЮ линию, ту, что выставил человек.
    // Считая по всей коллекции, мы дали бы соседней линии скрыть факт, что
    // искомых 12 °C в кадре нет.
    const main = contours.features.find(f => f.properties.base)
    setIsothermDrawn(main ? featureDrawn(main) : true)
    map.setIsotherms(TEMP_KEY, contours, labels)
  }, [sstGrid, isotherm, isothermSpread, isoEnabled])

  // Поверхностная изотерма: своя сетка (0 м), свой цвет, свой ключ слоёв.
  // Подписей у неё нет — её называет легенда, а вторая семья ярлыков поверх
  // первой читалась бы как одна.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const grid = surfaceEnabled ? toContourGrid(surfaceSource) : null
    if (!grid || isothermSurface == null) {
      map.removeIsotherms(SURFACE_KEY)
      setSurfaceDrawn(true)
      return
    }
    const { contours } = buildIsothermFamily(
      grid,
      [isothermSurface],
      isothermSurface,
      isothermSurfaceColor(),
    )
    const main = contours.features[0]
    setSurfaceDrawn(main ? featureDrawn(main) : true)
    map.setIsotherms(SURFACE_KEY, contours)
  }, [surfaceSource, isothermSurface, surfaceEnabled])

  // Градиенты (фронты): на смену сетки ИЛИ силы/единицы.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const grid = frontsEnabled ? toContourGrid(sstGrid) : null
    if (!grid || fronts == null) {
      map.removeFronts(TEMP_KEY)
      setFrontsDrawn(true)
      return
    }
    const { line, labels } = detectFronts(grid, toPer100km(fronts, frontsUnit))
    setFrontsDrawn(featureDrawn(line))
    map.setFronts(TEMP_KEY, line, labels)
  }, [sstGrid, fronts, frontsUnit, frontsEnabled])

  // Уход из раздела (размонтирование) → снять линии, иначе повиснут над другой картой.
  useEffect(
    () => () => {
      const map = mapRef.current
      map?.removeIsotherms(TEMP_KEY)
      map?.removeIsotherms(SURFACE_KEY)
      map?.removeFronts(TEMP_KEY)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // Пустая сетка — не ошибка: за дату в этом кадре данных может не быть вовсе.
  // Легенда об этом говорит вслух, иначе включённый тумблер и чистая карта.
  // Отдельная новость — поставка оборвалась ВНУТРИ кадра (прод 12.08.2026):
  // линии есть, но срезаны по прямой, и молчать об этом так же нельзя.
  return {
    sstData: gridHasData(sstGrid),
    sstPartial: gridIsPartial(sstGrid, sstCovered),
    isothermDrawn,
    frontsDrawn,
    surfaceData: gridHasData(surfaceSource),
    surfaceDrawn,
    // Обе сетки: кадр серии со снятой половиной линий — это картинка, о которой
    // потом не скажешь, чего на ней не хватает.
    pending:
      toolPending(loadedKey, overlayGridKey({ date, depth, viewKey, loadNonce }), gridEnabled) ||
      toolPending(
        surfLoadedKey,
        overlayGridKey({ date, depth: 0, viewKey, loadNonce }),
        surfaceGridEnabled,
      ),
  }
}
